'use client'

import { Button } from '@brew/ui/button'
import { cn } from '@brew/ui/cn'
import type { FileUIPart, SourceDocumentUIPart } from 'ai'
import {
  AudioLinesIcon,
  FileCodeIcon,
  FileIcon,
  FileJsonIcon,
  FileSearchIcon,
  FileSpreadsheetIcon,
  FileTextIcon,
  ImageIcon,
  Loader2Icon,
  type LucideIcon,
  PresentationIcon,
  VideoIcon,
  XIcon,
} from 'lucide-react'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import type { ComponentProps, HTMLAttributes, ReactNode } from 'react'
import { createContext, useContext } from 'react'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card'
import type { Messages } from '@/lib/i18n/messages'

type AttachmentData =
  | (FileUIPart & {
      id: string
      uploadStatus?: 'uploading' | 'uploaded' | 'error'
    })
  | (SourceDocumentUIPart & { id: string })

type AttachmentVariant = 'grid' | 'inline' | 'list' | 'stack'

type MediaCategory =
  | 'image'
  | 'video'
  | 'audio'
  | 'document'
  | 'source'
  | 'unknown'

type AttachmentsContextValue = {
  variant: AttachmentVariant
}

type AttachmentContextValue = {
  data: AttachmentData
  onRemove?: () => void
}

const AttachmentsContext = createContext<AttachmentsContextValue | null>(null)
const AttachmentContext = createContext<AttachmentContextValue | null>(null)

function useAttachmentsContext() {
  const context = useContext(AttachmentsContext)
  if (!context) {
    throw new Error(
      'Attachment components must be used within <Attachments />.'
    )
  }
  return context
}

function useAttachmentContext() {
  const context = useContext(AttachmentContext)
  if (!context) {
    throw new Error(
      'Attachment child components must be used within <Attachment />.'
    )
  }
  return context
}

export function getMediaCategory(data: AttachmentData): MediaCategory {
  if (data.type === 'source-document') {
    return 'source'
  }

  if (data.mediaType.startsWith('image/')) {
    return 'image'
  }
  if (data.mediaType.startsWith('video/')) {
    return 'video'
  }
  if (data.mediaType.startsWith('audio/')) {
    return 'audio'
  }
  if (
    data.mediaType.includes('pdf') ||
    data.mediaType.startsWith('message/') ||
    data.mediaType.includes('text/') ||
    data.mediaType.includes('document') ||
    data.mediaType.includes('sheet') ||
    data.mediaType.includes('presentation') ||
    data.mediaType.includes('json')
  ) {
    return 'document'
  }

  return 'unknown'
}

/** `useTranslations('chat')`: the attachment words live in `attachments`. */
type AttachmentTranslator = ReturnType<typeof useTranslations<'chat'>>

export function getAttachmentLabel(
  t: AttachmentTranslator,
  data: AttachmentData
): string {
  if (data.type === 'source-document') {
    return data.filename || data.title || t('attachments.sourceDocument')
  }

  return (
    data.filename ||
    t(
      getMediaCategory(data) === 'image'
        ? 'attachments.image'
        : 'attachments.attachment'
    )
  )
}

/**
 * A media type for a reader: a format's own name (the same in every
 * language), an image format ("PNG image"), or a kind of document in the
 * reader's words (`attachments.mediaTypes`).
 */
type FriendlyMediaType =
  | { name: string }
  | { imageFormat: string }
  | { label: keyof Messages['chat']['attachments']['mediaTypes'] }

// Raw IANA media types are noise to a reader ("application/vnd.openxml…").
// Map the common ones to a friendly, human label and derive a sensible
// fallback from the subtype for anything not enumerated.
const FRIENDLY_MEDIA_TYPES: Readonly<Record<string, FriendlyMediaType>> = {
  'application/pdf': { name: 'PDF' },
  'image/png': { imageFormat: 'PNG' },
  'image/jpeg': { imageFormat: 'JPEG' },
  'image/jpg': { imageFormat: 'JPEG' },
  'image/gif': { imageFormat: 'GIF' },
  'image/webp': { imageFormat: 'WebP' },
  'image/svg+xml': { imageFormat: 'SVG' },
  'image/heic': { imageFormat: 'HEIC' },
  'text/html': { name: 'HTML' },
  'text/mjml': { name: 'MJML' },
  'text/jsx': { name: 'React Email' },
  'message/rfc822': { label: 'emailMessage' },
  'text/plain': { label: 'text' },
  'text/markdown': { name: 'Markdown' },
  'text/csv': { name: 'CSV' },
  'application/json': { name: 'JSON' },
  'application/msword': { label: 'wordDocument' },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': {
    label: 'wordDocument',
  },
  'application/vnd.ms-excel': { label: 'spreadsheet' },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': {
    label: 'spreadsheet',
  },
  'application/vnd.ms-powerpoint': { label: 'presentation' },
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': {
    label: 'presentation',
  },
  'application/zip': { label: 'archive' },
}

function describeFriendlyMediaType(
  t: AttachmentTranslator,
  friendly: FriendlyMediaType
): string {
  if ('name' in friendly) {
    return friendly.name
  }
  if ('imageFormat' in friendly) {
    return t('attachments.imageOfFormat', { format: friendly.imageFormat })
  }
  return t(`attachments.mediaTypes.${friendly.label}`)
}

function getFriendlyMediaType(
  t: AttachmentTranslator,
  data: AttachmentData
): string {
  if (data.type === 'source-document') {
    return t('attachments.source')
  }
  const mediaType = data.mediaType ?? ''
  const friendly = FRIENDLY_MEDIA_TYPES[mediaType]
  if (friendly) {
    return describeFriendlyMediaType(t, friendly)
  }
  if (mediaType.startsWith('image/')) {
    return t('attachments.imageOfFormat', {
      format: mediaType
        .slice('image/'.length)
        .replace('+xml', '')
        .toUpperCase(),
    })
  }
  const slashIndex = mediaType.indexOf('/')
  if (slashIndex !== -1) {
    const subtype = mediaType
      .slice(slashIndex + 1)
      .replace(/^x-/, '')
      .replace(/^vnd\..*$/, '')
    if (subtype) {
      return subtype.toUpperCase()
    }
  }
  return t('attachments.file')
}

// Type-aware icon + tint for the document tile shown in the `stack`
// variant, so a PDF reads as a PDF (red doc) and a sheet as a sheet
// (green grid) at a glance instead of every file sharing one grey icon.
function getFileVisual(data: AttachmentData): {
  Icon: LucideIcon
  tint: string
} {
  const category = getMediaCategory(data)
  const mediaType = data.type === 'file' ? (data.mediaType ?? '') : ''

  if (category === 'source') {
    return {
      Icon: FileSearchIcon,
      tint: 'bg-sky-500/10 text-sky-600 dark:text-sky-400',
    }
  }
  if (category === 'video') {
    return {
      Icon: VideoIcon,
      tint: 'bg-fuchsia-500/10 text-fuchsia-600 dark:text-fuchsia-400',
    }
  }
  if (category === 'audio') {
    return {
      Icon: AudioLinesIcon,
      tint: 'bg-fuchsia-500/10 text-fuchsia-600 dark:text-fuchsia-400',
    }
  }
  if (mediaType.includes('pdf')) {
    return {
      Icon: FileTextIcon,
      tint: 'bg-red-500/10 text-red-600 dark:text-red-400',
    }
  }
  if (
    mediaType.includes('sheet') ||
    mediaType.includes('excel') ||
    mediaType.includes('csv')
  ) {
    return {
      Icon: FileSpreadsheetIcon,
      tint: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    }
  }
  if (mediaType.includes('presentation') || mediaType.includes('powerpoint')) {
    return {
      Icon: PresentationIcon,
      tint: 'bg-orange-500/10 text-orange-600 dark:text-orange-400',
    }
  }
  if (mediaType.includes('json')) {
    return {
      Icon: FileJsonIcon,
      tint: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
    }
  }
  if (
    mediaType.includes('html') ||
    mediaType.includes('xml') ||
    mediaType.includes('mjml') ||
    mediaType.includes('jsx') ||
    mediaType.includes('rfc822') ||
    mediaType.includes('javascript') ||
    mediaType.includes('css') ||
    mediaType.includes('code')
  ) {
    return {
      Icon: FileCodeIcon,
      tint: 'bg-violet-500/10 text-violet-600 dark:text-violet-400',
    }
  }
  if (mediaType.includes('word') || mediaType.includes('document')) {
    return {
      Icon: FileTextIcon,
      tint: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
    }
  }
  if (mediaType.startsWith('text/')) {
    return {
      Icon: FileTextIcon,
      tint: 'bg-slate-500/10 text-slate-600 dark:text-slate-400',
    }
  }
  if (category === 'image') {
    return {
      Icon: ImageIcon,
      tint: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400',
    }
  }
  return { Icon: FileIcon, tint: 'bg-muted text-muted-foreground' }
}

function getAttachmentSubtitle(
  t: AttachmentTranslator,
  data: AttachmentData
): string | null {
  return getFriendlyMediaType(t, data)
}

function renderCategoryIcon(category: MediaCategory, className: string) {
  switch (category) {
    case 'image':
      return <ImageIcon className={className} />
    case 'video':
      return <VideoIcon className={className} />
    case 'audio':
      return <AudioLinesIcon className={className} />
    case 'document':
      return <FileTextIcon className={className} />
    case 'source':
      return <FileSearchIcon className={className} />
    default:
      return <FileIcon className={className} />
  }
}

function isPreviewableImage(data: AttachmentData) {
  return (
    data.type === 'file' &&
    getMediaCategory(data) === 'image' &&
    Boolean(data.url)
  )
}

export type AttachmentsProps = HTMLAttributes<HTMLDivElement> & {
  variant?: AttachmentVariant
}

export function Attachments({
  className,
  variant = 'grid',
  children,
  ...props
}: AttachmentsProps) {
  return (
    <AttachmentsContext.Provider value={{ variant }}>
      <div
        className={cn(
          variant === 'grid' && 'flex flex-wrap gap-2',
          variant === 'inline' && 'flex flex-wrap items-start gap-2',
          variant === 'list' && 'flex flex-col gap-2',
          variant === 'stack' && 'flex w-full flex-col items-end gap-2',
          className
        )}
        {...props}
      >
        {children}
      </div>
    </AttachmentsContext.Provider>
  )
}

export type AttachmentProps = HTMLAttributes<HTMLDivElement> & {
  data: AttachmentData
  onRemove?: () => void
}

export function Attachment({
  className,
  data,
  onRemove,
  children,
  ...props
}: AttachmentProps) {
  const { variant } = useAttachmentsContext()
  const isGridImage = variant === 'grid' && isPreviewableImage(data)
  const isStackImage = variant === 'stack' && isPreviewableImage(data)

  return (
    <AttachmentContext.Provider value={{ data, onRemove }}>
      <div
        className={cn(
          'group relative overflow-hidden border border-border bg-background text-foreground transition-colors',
          variant === 'grid' &&
            'flex h-28 w-28 flex-col justify-end rounded-lg bg-muted/30',
          isGridImage &&
            'h-auto w-fit max-w-[min(24rem,70vw)] rounded-lg bg-background',
          variant === 'inline' &&
            'flex min-w-0 max-w-full items-center gap-2 rounded-lg px-2.5 py-2 text-sm hover:bg-muted/60',
          variant === 'list' &&
            'flex items-center gap-3 rounded-lg px-3 py-3 hover:bg-muted/40',
          variant === 'stack' &&
            'flex w-[min(20rem,75vw)] max-w-full items-center gap-3 rounded-lg bg-card px-3 py-2.5 shadow-sm',
          isStackImage && 'flex-col items-stretch gap-0 p-0',
          className
        )}
        {...props}
      >
        {children}
      </div>
    </AttachmentContext.Provider>
  )
}

export type AttachmentPreviewProps = HTMLAttributes<HTMLDivElement> & {
  fallbackIcon?: ReactNode
}

export function AttachmentPreview({
  className,
  fallbackIcon,
  ...props
}: AttachmentPreviewProps) {
  const t = useTranslations('chat')
  const { variant } = useAttachmentsContext()
  const { data } = useAttachmentContext()
  const category = getMediaCategory(data)
  const label = getAttachmentLabel(t, data)
  const isGridImage = variant === 'grid' && isPreviewableImage(data)
  const isStackImage = variant === 'stack' && isPreviewableImage(data)
  const isUploading = data.type === 'file' && data.uploadStatus === 'uploading'
  let imageWidth = 40
  let imageHeight = 40
  let imageSizes = '40px'
  if (isGridImage) {
    imageWidth = 1024
    imageHeight = 1024
    imageSizes = '(max-width: 768px) 100vw, 20rem'
  } else if (isStackImage) {
    imageWidth = 640
    imageHeight = 480
    imageSizes = '(max-width: 768px) 75vw, 20rem'
  } else if (variant === 'list') {
    imageWidth = 48
    imageHeight = 48
    imageSizes = '48px'
  }

  if (isPreviewableImage(data)) {
    return (
      <div
        className={cn(
          'overflow-hidden',
          variant === 'grid' && 'absolute inset-0',
          isGridImage &&
            'relative flex max-h-[20rem] items-center justify-center bg-muted/20',
          isStackImage &&
            'relative flex max-h-56 min-h-[7rem] w-full items-center justify-center bg-muted/20',
          variant === 'inline' && 'size-14 shrink-0 rounded-lg border bg-muted',
          variant === 'list' && 'size-12 shrink-0 rounded-lg border bg-muted',
          className
        )}
        {...props}
      >
        <Image
          alt={label}
          className={cn(
            'size-full object-cover',
            isGridImage &&
              'h-auto max-h-[20rem] w-auto max-w-full object-contain',
            isStackImage && 'h-auto max-h-56 w-full object-contain'
          )}
          src={data.type === 'file' ? data.url : ''}
          unoptimized
          width={imageWidth}
          height={imageHeight}
          sizes={imageSizes}
          quality={isGridImage || isStackImage ? 75 : 60}
        />
        {isUploading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-background/45 backdrop-blur-[1px]">
            <div className="flex size-6 items-center justify-center rounded-lg bg-background/90 shadow-sm">
              <Loader2Icon className="size-3.5 animate-spin text-foreground/80" />
            </div>
          </div>
        ) : null}
      </div>
    )
  }

  const isStack = variant === 'stack'
  const { Icon: FileVisualIcon, tint: fileVisualTint } = getFileVisual(data)

  return (
    <div
      className={cn(
        'flex items-center justify-center',
        !isStack && 'text-muted-foreground',
        variant === 'grid' &&
          'absolute inset-0 bg-gradient-to-br from-muted to-muted/40',
        variant === 'inline' &&
          'size-14 shrink-0 rounded-lg border bg-muted/60',
        variant === 'list' && 'size-12 shrink-0 rounded-lg border bg-muted/60',
        isStack && cn('relative size-11 shrink-0 rounded-lg', fileVisualTint),
        className
      )}
      {...props}
    >
      {fallbackIcon ??
        (isStack ? (
          <FileVisualIcon className="size-5" />
        ) : (
          renderCategoryIcon(category, variant === 'grid' ? 'size-8' : 'size-4')
        ))}
      {isUploading ? (
        <Loader2Icon
          className={cn(
            'absolute size-3 animate-spin',
            isStack
              ? 'top-1 right-1'
              : 'top-1.5 right-1.5 text-muted-foreground'
          )}
        />
      ) : null}
    </div>
  )
}

export type AttachmentInfoProps = HTMLAttributes<HTMLDivElement> & {
  showMediaType?: boolean
  description?: string | null
}

export function AttachmentInfo({
  className,
  showMediaType = false,
  description,
  ...props
}: AttachmentInfoProps) {
  const t = useTranslations('chat')
  const { variant } = useAttachmentsContext()
  const { data } = useAttachmentContext()
  const label = getAttachmentLabel(t, data)
  const subtitle = getAttachmentSubtitle(t, data)

  const isStackImage = variant === 'stack' && isPreviewableImage(data)

  return (
    <div
      className={cn(
        'min-w-0',
        variant === 'grid' && '',
        variant === 'grid' &&
          isPreviewableImage(data) &&
          'border-border border-t px-3 py-2',
        variant === 'inline' && 'min-w-0 flex-1',
        variant === 'list' && 'min-w-0 flex-1',
        variant === 'stack' && 'min-w-0 flex-1',
        isStackImage && 'flex-none border-border border-t px-3 py-2',
        className
      )}
      {...props}
    >
      <div
        className={cn(
          'truncate font-medium',
          variant === 'stack' ? 'text-sm' : 'text-xs'
        )}
      >
        {label}
      </div>
      {showMediaType && subtitle ? (
        <div className="truncate text-caption text-muted-foreground">
          {subtitle}
        </div>
      ) : null}
      {description ? (
        <div className="mt-1 line-clamp-2 text-caption text-muted-foreground">
          {description}
        </div>
      ) : null}
    </div>
  )
}

export type AttachmentRemoveProps = ComponentProps<typeof Button> & {
  label?: string
}

export function AttachmentRemove({
  className,
  label: labelOverride,
  ...props
}: AttachmentRemoveProps) {
  const tCommon = useTranslations('common')
  const label = labelOverride ?? tCommon('actions.remove')
  const { variant } = useAttachmentsContext()
  const { onRemove } = useAttachmentContext()

  if (!onRemove) {
    return null
  }

  return (
    <Button
      aria-label={label}
      className={cn(
        'shrink-0',
        variant === 'grid' && '',
        variant === 'inline' &&
          'size-6 p-0 text-muted-foreground hover:text-foreground',
        variant === 'list' &&
          'size-7 p-0 text-muted-foreground hover:text-foreground',
        className
      )}
      onClick={(event) => {
        event.stopPropagation()
        onRemove()
      }}
      type="button"
      variant="ghost"
      {...props}
    >
      <XIcon className="size-3.5" />
      <span className="sr-only">{label}</span>
    </Button>
  )
}

export type AttachmentHoverCardProps = ComponentProps<typeof HoverCard>

export function AttachmentHoverCard({
  openDelay = 0,
  closeDelay = 0,
  ...props
}: AttachmentHoverCardProps) {
  return <HoverCard openDelay={openDelay} closeDelay={closeDelay} {...props} />
}

export type AttachmentHoverCardTriggerProps = ComponentProps<
  typeof HoverCardTrigger
>

export function AttachmentHoverCardTrigger(
  props: AttachmentHoverCardTriggerProps
) {
  return <HoverCardTrigger {...props} />
}

export type AttachmentHoverCardContentProps = ComponentProps<
  typeof HoverCardContent
>

export function AttachmentHoverCardContent({
  align = 'start',
  ...props
}: AttachmentHoverCardContentProps) {
  return <HoverCardContent align={align} {...props} />
}

export type AttachmentEmptyProps = HTMLAttributes<HTMLDivElement>

export function AttachmentEmpty({
  className,
  children,
  ...props
}: AttachmentEmptyProps) {
  const t = useTranslations('chat')
  return (
    <div
      className={cn(
        'rounded-lg border border-border border-dashed px-3 py-2 text-muted-foreground text-sm',
        className
      )}
      {...props}
    >
      {children ?? t('attachments.empty')}
    </div>
  )
}
